import React from 'react';
import {
  BrowserRouter, Routes, Route, Navigate,
  useLocation, useNavigate
} from 'react-router-dom';
import {
  ThemeProvider, CssBaseline, Box, BottomNavigation,
  BottomNavigationAction, AppBar, Toolbar, Typography, IconButton
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Vaccines, CameraAlt, Logout
} from '@mui/icons-material';
import { AuthProvider, useAuth } from './context/AuthContext';
import theme from './theme';
import Login from './screens/Login';
import Dashboard from './screens/Dashboard';
import Identify from './screens/Identify';
import Vaccinate from './screens/Vaccinate';
import { initOfflineSync } from './utils/offlineQueue';
import client from './api/client';

initOfflineSync(client);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Layout({ children }) {
  const { logoutUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { label: 'Identify', icon: <CameraAlt />, path: '/identify' },
    { label: 'Vaccinate', icon: <Vaccines />, path: '/vaccinate' },
  ];

  const currentNav = navItems.findIndex(
    item => item.path === location.pathname
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" elevation={0} sx={{
        background: 'linear-gradient(135deg, #5BA4D4 0%, #3A7EAA 100%)'
      }}>
        <Toolbar>
          <img
            src="/logo.png" alt="Furry AI"
            style={{ height: 32, marginRight: 8 }}
          />
          <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
            Furry AI
          </Typography>
          <IconButton color="inherit" onClick={logoutUser} size="small">
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, mt: 8, mb: 8, overflowY: 'auto' }}>
        {children}
      </Box>

      <BottomNavigation
        value={currentNav}
        onChange={(_, v) => navigate(navItems[v].path)}
        sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }}
      >
        {navItems.map(item => (
          <BottomNavigationAction
            key={item.path}
            label={item.label}
            icon={item.icon}
          />
        ))}
      </BottomNavigation>
    </Box>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <Login />}
      />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/identify" element={
        <ProtectedRoute>
          <Layout><Identify /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/vaccinate" element={
        <ProtectedRoute>
          <Layout><Vaccinate /></Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
